import React, { useState, useEffect, useRef } from 'react';
import {
  Typography,
  Paper,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  Link,
  TablePagination
} from '@mui/material';
import { apiClient } from '../api/apiClient';
import { useNavigate } from 'react-router-dom';

const DEBUG = true;

const debug = (message, data = null) => {
  if (!DEBUG) return;
  const stack = new Error().stack;
  const caller = stack.split('\n')[2].trim();
  const timestamp = new Date().toISOString().split('T')[1].slice(0, -1);
  console.log(`[${timestamp}][Gerichte] ${message}`, data ? data : '');
  console.log('Caller:', caller);
};

const BUNDESLAENDER = [
    { code: "BWB", name: "Baden-Württemberg" },
    { code: "FSB", name: "Bayern" },
    { code: "BLN", name: "Berlin" },
    { code: "BRB", name: "Brandenburg" },
    { code: "BRE", name: "Bremen" },
    { code: "HAM", name: "Hamburg" },
    { code: "HES", name: "Hessen" },
    { code: "MVP", name: "Mecklenburg-Vorpommern" },
    { code: "NSB", name: "Niedersachsen" },
    { code: "NRW", name: "Nordrhein-Westfalen" },
    { code: "RPF", name: "Rheinland-Pfalz" },
    { code: "SAA", name: "Saarland" },
    { code: "FSS", name: "Sachsen" },
    { code: "SAN", name: "Sachsen-Anhalt" },
    { code: "SHS", name: "Schleswig-Holstein" },
    { code: "FST", name: "Thüringen" },
    { code: "BRD", name: "Bundesrepublik Deutschland" }
];

const Gerichte = () => {
  debug('Component wird initialisiert');
  
  const [allGerichte, setAllGerichte] = useState([]); // Alle Gerichte
  const [filteredGerichte, setFilteredGerichte] = useState([]); // Gefilterte Liste
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [bundesland, setBundesland] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [searchOrt, setSearchOrt] = useState('');
  const [orderBy, setOrderBy] = useState('bezeichnung'); // Standard-Sortierung nach Name
  const [order, setOrder] = useState('asc'); // 'asc' oder 'desc'
  const initialLoadDoneRef = useRef(false);
  const isLoadingRef = useRef(false);
  const navigate = useNavigate();

  // Einmaliges Laden aller Gerichte
  const fetchGerichte = async () => {
    if (isLoadingRef.current) {
      debug('Überspringe API-Call - bereits ladend');
      return;
    }

    try {
      isLoadingRef.current = true;
      debug('API Request wird gesendet');
      const response = await apiClient.get('/gerichte/');
      debug('API Response erhalten', { count: response.data.length });
      
      setAllGerichte(response.data);
      setFilteredGerichte(response.data);
      initialLoadDoneRef.current = true;
    } catch (error) {
      console.error('Fehler beim Laden der Gerichte:', error);
      initialLoadDoneRef.current = false;
    } finally {
      isLoadingRef.current = false;
    }
  };

  // Initiales Laden - nur einmal beim Mounten
  useEffect(() => {
    if (!initialLoadDoneRef.current) {
      fetchGerichte();
    }
  }, []);

  // Filtern der Gerichte basierend auf den Suchkriterien
  useEffect(() => {
    const filtered = allGerichte.filter(gericht => {
      const matchesName = gericht.bezeichnung?.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesOrt = gericht.ort?.toLowerCase().includes(searchOrt.toLowerCase());
      const matchesBundesland = !bundesland || gericht.bundesland === bundesland;
      
      return matchesName && matchesOrt && matchesBundesland;
    });
    
    setFilteredGerichte(filtered);
    setPage(0); // Zurück zur ersten Seite bei Filteränderung
  }, [allGerichte, searchTerm, searchOrt, bundesland]);

  // Pagination Handler
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleBundeslandChange = (e) => {
    setBundesland(e.target.value);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleOrtChange = (e) => {
    setSearchOrt(e.target.value);
  };

  // Sortierungsfunktion
  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  // Sortiere die gefilterten Daten
  const sortedData = React.useMemo(() => {
    const comparator = (a, b) => {
      let aValue = a[orderBy];
      let bValue = b[orderBy];

      // Spezielle Behandlung für Bundesland
      if (orderBy === 'bundesland') {
        aValue = BUNDESLAENDER.find(land => land.code === a.bundesland)?.name || a.bundesland;
        bValue = BUNDESLAENDER.find(land => land.code === b.bundesland)?.name || b.bundesland;
      }

      // Null-Werte ans Ende sortieren
      if (aValue === null || aValue === undefined) return 1;
      if (bValue === null || bValue === undefined) return -1;

      // Vergleich
      if (order === 'asc') {
        return aValue.toString().localeCompare(bValue.toString(), 'de');
      } else {
        return bValue.toString().localeCompare(aValue.toString(), 'de');
      }
    };

    return [...filteredGerichte].sort(comparator);
  }, [filteredGerichte, order, orderBy]);

  // Berechne die aktuelle Seite der sortierten und gefilterten Daten
  const paginatedGerichte = sortedData.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  // Sortier-Header-Komponente
  const SortLabel = ({ property, label }) => {
    const isActive = orderBy === property;
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          cursor: 'pointer',
          userSelect: 'none',
          '&:hover': {
            opacity: 0.7
          }
        }}
        onClick={() => handleRequestSort(property)}
      >
        {label}
        <Box sx={{ ml: 0.5, display: 'flex', flexDirection: 'column', height: '16px' }}>
          <Box
            component="span"
            sx={{
              width: 0,
              height: 0,
              borderLeft: '4px solid transparent',
              borderRight: '4px solid transparent',
              borderBottom: '4px solid',
              opacity: isActive && order === 'asc' ? 1 : 0.3,
              mb: '2px'
            }}
          />
          <Box
            component="span"
            sx={{
              width: 0,
              height: 0,
              borderLeft: '4px solid transparent',
              borderRight: '4px solid transparent',
              borderTop: '4px solid',
              opacity: isActive && order === 'desc' ? 1 : 0.3
            }}
          />
        </Box>
      </Box>
    );
  };

  const handleRowClick = (id) => {
    navigate(`/gerichte/${id}`);
  };

  if (isLoadingRef.current) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>Lade Gerichte...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      width: '70%', 
      margin: '0 auto',
      p: 3
    }}>
      <Typography variant="h4" gutterBottom>
        Gerichte
      </Typography>

      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Name des Gerichts"
              variant="outlined"
              value={searchTerm}
              onChange={handleSearchChange}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Ort"
              variant="outlined"
              value={searchOrt}
              onChange={handleOrtChange}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth variant="outlined">
              <InputLabel>Bundesland</InputLabel>
              <Select
                value={bundesland}
                onChange={handleBundeslandChange}
                label="Bundesland"
              >
                <MenuItem value="">Alle</MenuItem>
                {BUNDESLAENDER.map((land) => (
                  <MenuItem key={land.code} value={land.code}>
                    {land.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>
                <SortLabel property="bezeichnung" label="Name des Gerichts" />
              </TableCell>
              <TableCell>
                <SortLabel property="bundesland" label="Bundesland" />
              </TableCell>
              <TableCell>
                <SortLabel property="ort" label="Ort" />
              </TableCell>
              <TableCell>Homepage</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedGerichte.map((gericht) => (
              <TableRow 
                key={gericht.id}
                hover
                onClick={() => handleRowClick(gericht.id)}
                sx={{ cursor: 'pointer' }}
              >
                <TableCell>{gericht.bezeichnung}</TableCell>
                <TableCell>
                  {BUNDESLAENDER.find(land => land.code === gericht.bundesland)?.name || gericht.bundesland}
                </TableCell>
                <TableCell>{gericht.ort}</TableCell>
                <TableCell>
                  {gericht.homepage && (
                    <Link href={gericht.homepage} target="_blank" rel="noopener noreferrer">
                      {gericht.homepage}
                    </Link>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        rowsPerPageOptions={[10, 25, 50]}
        component="div"
        count={filteredGerichte.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        labelRowsPerPage="Zeilen pro Seite:"
        labelDisplayedRows={({ from, to, count }) => `${from}-${to} von ${count}`}
      />
    </Box>
  );
};

export default Gerichte; 